import {
  toWSDateString,
  WSTableCell,
  WSTableColumn
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { Engagement, PayeeRow } from "@wingspanhq/wingspan-api-sdk";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { selectorSearchRowSecondaryName } from "../../../../shared/selectors/selectorSearchRowSecondaryName";
import { WorkLogPayerStatusCell } from "../WorkLogPayerStatusCell";
import { PayPeriodCell } from "./PayPeriodCell";
import { TableItemData } from "./TableWorkLogsPayerView";

type ColumnsConfig = {
  payeesMap: Map<string, PayeeRow>;
  engagementsMap: Map<string, Engagement>;
};

export const getColumns = ({
  payeesMap,
  engagementsMap
}: ColumnsConfig): WSTableColumn<TableItemData>[] => [
  // Pay period leads: the design makes it the headline grouping for work logs,
  // and it is what a payer scans when reviewing a cycle.
  {
    config: {
      header: {
        text: "Pay period"
      }
    },
    renderFunction: item => (
      <PayPeriodCell payPeriodId={item.data.payPeriodId} />
    )
  },
  {
    config: {
      header: {
        text: "Name"
      }
    },
    renderFunction: item => {
      // TODO(v3): payee lookup keyed on `payeeEngagementId` (v1 used `payeeId`).
      const payee = payeesMap.get(item.data.payeeEngagementId ?? "");
      if (!payee) {
        return <WSTableCell text="-" />;
      }
      return (
        <WSTableCell
          text={selectorSearchRowName(payee)}
          secondaryText={selectorSearchRowSecondaryName(payee)}
          truncationOptions={{
            text: { allowTwoLines: true }
          }}
        />
      );
    }
  },
  {
    config: {
      header: {
        text: "Work log #"
      }
    },
    renderFunction: item => <WSTableCell text={item.data.workLogNumber} />
  },
  {
    config: {
      header: {
        text: "Amount"
      }
    },
    renderFunction: item => (
      <WSTableCell text={formatMoney(item.data.totalAmount ?? 0, "USD")} />
    )
  },
  {
    config: {
      header: {
        text: "Last modified"
      }
    },
    // TODO(v3): v3 WorkLog has no `updatedAt`; approximate "Last modified" with
    // the in-review event timestamp.
    renderFunction: item => (
      <WSTableCell
        text={toWSDateString(item.data.events?.inReviewAt, "timeframe")}
      />
    )
  },
  {
    config: {
      header: {
        text: "Status"
      }
    },
    renderFunction: item => {
      // The engagement decides whether an unapproved work log is described as
      // becoming a pay statement or an invoice. The search row stopped carrying
      // the type (WIN-16077), so it is read from the engagement template the row
      // points at — which only the payer's own seat can load.
      const payee = payeesMap.get(item.data.payeeEngagementId ?? "");
      const rowEngagement = (payee?.engagements ?? []).find(
        engagement => engagement.id === item.data.payeeEngagementId
      );
      const engagementType = engagementsMap.get(
        rowEngagement?.engagementId ?? ""
      )?.engagementType;

      return (
        <WorkLogPayerStatusCell
          workLog={item.data}
          engagementType={engagementType}
        />
      );
    }
  }
];
